/*
 * tp-track.js — Storefront Event Tracking (Spec 195).
 * Abhaengigkeitsfreies IIFE, ES5. Oeffentliche API: window.TP.track(type, props).
 *
 * Sammelt Interaktions-Events (Bubble geoeffnet, Collection geklickt, Add-to-Cart) und
 * streamt sie per navigator.sendBeacon ueber den Shopify App Proxy an storefronthub.
 * Stempelt zusaetzlich die visitor_id in die Cart-note_attributes (verbindliche
 * Kauf-Bruecke, §5). Datenvertrag: events-tracking-backend.md §2 (Source of Truth).
 *
 * Identitaeten (§4):  visitor_id (localStorage, dauerhaft), session_id (sessionStorage),
 *                     cart_token (sekundaerer Fallback), client_event_id (je Event neu).
 */
(function () {
  'use strict';

  var CFG = window.TP_CFG || {};
  var ENDPOINT = CFG.endpoint || '/apps/storefronthub/events';
  // Client-seitige Whitelist (spiegelt Backend §2.4) — nur bekannte Typen queuen.
  // 'purchase' bewusst NICHT enthalten: Kaeufe kommen serverseitig aus der Order.
  var ALLOWED = {
    bubble_opened: 1, story_viewed: 1, story_card_viewed: 1, collection_clicked: 1,
    product_viewed: 1, add_to_cart: 1, addon_added: 1, checkout_started: 1,
    trustbar_click: 1,
    // Story-Viewer v2 (specs/002-story-viewer-v2.md §7.2). Server-Whitelist muss VORHER
    // deployt sein (marketplace storefronthub-rest handlers/events.rs), sonst still verworfen.
    story_closed: 1, story_product_clicked: 1, story_shelf_expanded: 1, story_quiz_answered: 1,
    story_quiz_skipped: 1, story_code_used: 1, story_label_opened: 1, story_video_progress: 1,
    story_article_clicked: 1,
    // Merkliste (Plan 0071 D8)
    product_liked: 1, product_unliked: 1, device_registered: 1, price_alert_set: 1,
    // Explorer (Plan 0077 D10) — Server-Whitelist zuerst (handlers/events.rs)
    explorer_offer_opened: 1, explorer_viewed: 1, explorer_preset_selected: 1,
    explorer_filter_changed: 1, explorer_weights_changed: 1, explorer_product_clicked: 1,
    explorer_empty: 1,
    // Ankauf-Einstieg (marketplace Plan 0249 E3) — Server-Whitelist zuerst (handlers/events.rs)
    ankauf_clicked: 1
  };
  var MAX_BATCH = 25;      // unter Server-Max 50 (Puffer fuer gleichzeitige Flushes)
  var FLUSH_MS = 5000;
  var MAX_PROPS_BYTES = 2048;   // §9: props <= 2 KB

  var queue = [];
  var cartToken = null;

  // ES5-UUID v4, Fallback wenn crypto.randomUUID fehlt (kein Math.random als Identitaet).
  function uuid() {
    if (window.crypto && crypto.randomUUID) { return crypto.randomUUID(); }
    var b = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
    var h = []; for (var i = 0; i < 16; i++) { h.push((b[i] + 0x100).toString(16).slice(1)); }
    return h[0] + h[1] + h[2] + h[3] + '-' + h[4] + h[5] + '-' + h[6] + h[7] + '-' +
           h[8] + h[9] + '-' + h[10] + h[11] + h[12] + h[13] + h[14] + h[15];
  }

  function getId(store, key) {
    try {
      var v = store.getItem(key);
      if (!v) { v = uuid(); store.setItem(key, v); }
      return v;
    } catch (e) { return uuid(); }
  }

  var visitorId = getId(localStorage, 'tp:track:vid');
  var sessionId = getId(sessionStorage, 'tp:track:sid');

  function consentOk() { return !CFG.requireConsent || window.TP_CONSENT === true; }

  // cart_token lazy aus /cart.js, gecached. Nur sekundaerer Fallback (§4) — Shopify hat
  // ihn deprecatet. Leerer Warenkorb kann token:null liefern; dann bleibt das Feld weg.
  function primeCart() {
    try {
      var cached = sessionStorage.getItem('tp:track:cart');
      if (cached) { cartToken = cached; return; }
    } catch (e) {}
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        if (c && c.token) {
          // /cart.js liefert den Token neuerdings mit ?key=… — den key-Teil abschneiden.
          cartToken = String(c.token).split('?')[0];
          try { sessionStorage.setItem('tp:track:cart', cartToken); } catch (e) {}
        }
      })
      .catch(function () {});
  }

  // Cart-Attribute-Stamping (§5): visitor_id in die Cart → landet spaeter in
  // order.note_attributes und ist die primaere Kauf-Bruecke. Einmal pro Session,
  // hinter dem Consent-Gate. Schluesselnamen verbindlich (Backend §2.5).
  function ensureStamp() {
    if (!consentOk()) { return; }
    try { if (sessionStorage.getItem('tp:track:stamped')) { return; } } catch (e) {}
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _tp_visitor_id: visitorId, _tp_session_id: sessionId } })
    }).then(function () {
      try { sessionStorage.setItem('tp:track:stamped', '1'); } catch (e) {}
    }).catch(function () {});
  }

  // props klein halten (§9): zu grosse Objekte verwerfen statt halb zu senden.
  function cleanProps(props) {
    if (!props) { return undefined; }
    try {
      if (JSON.stringify(props).length > MAX_PROPS_BYTES) { return undefined; }
    } catch (e) { return undefined; }
    return props;
  }

  function track(type, props) {
    if (!ALLOWED[type] || !consentOk()) { return; }
    queue.push({
      type: type,
      client_event_id: uuid(),
      occurred_at: Date.now(),
      visitor_id: visitorId,
      session_id: sessionId,
      cart_token: cartToken || undefined,
      url: location.pathname,        // §9: ohne Query (PII/Limit)
      props: cleanProps(props)
    });
    if (queue.length >= MAX_BATCH) { flush(); }
  }

  function flush() {
    if (!queue.length) { return; }
    var batch = queue.splice(0, MAX_BATCH);
    var body = JSON.stringify({ events: batch });
    var sent = false;
    try {
      // Blob-Type garantiert application/json fuer den Axum-Json-Extractor (§2.1).
      var blob = new Blob([body], { type: 'application/json' });
      sent = navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, blob);
    } catch (e) {}
    if (!sent) {
      // Fallback wenn sendBeacon fehlt oder die Queue voll war. Best-effort, kein Retry.
      try {
        fetch(ENDPOINT, {
          method: 'POST', keepalive: true,
          headers: { 'Content-Type': 'application/json' }, body: body
        }).catch(function () {});
      } catch (e) {}
    }
  }

  setInterval(flush, FLUSH_MS);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') { flush(); }
  });
  // Doppel-Flush (visibilitychange + pagehide) ist okay — das Backend dedupt ueber
  // client_event_id (Backend §3). Wir verhindern es nicht clientseitig.
  window.addEventListener('pagehide', flush);

  primeCart();
  ensureStamp();   // §5: frueh stempeln, einmal pro Session

  window.TP = { track: track, refreshCart: primeCart, ensureStamp: ensureStamp };
})();
